import { SketchPicker, ColorResult, RGBColor } from 'react-color';
import { MdColorize } from 'react-icons/md';

interface ColorPickerProps {
  label: string;
  color: string;
  opacity?: number;
  isOpen: boolean;
  position: { x: number; y: number };
  onOpen: (buttonElement: HTMLButtonElement) => void;
  onClose: () => void;
  onChange: (color: string, opacity?: number) => void;
  onPickColor?: () => void;
  isPickingColor?: boolean;
  showMultipleValues?: boolean;
}

export function ColorPicker({
  label,
  color,
  opacity = 1,
  isOpen,
  position,
  onOpen,
  onClose,
  onChange,
  onPickColor,
  isPickingColor,
  showMultipleValues = false,
}: ColorPickerProps) {
  // Convert hex color and opacity to RGBA for the color picker
  const getRgbaFromHexAndOpacity = (hex: string, opacity: number): RGBColor => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');
    
    // Handle both short (3 chars) and full (6 chars) hex
    const r = cleanHex.length === 3 
      ? parseInt(cleanHex[0] + cleanHex[0], 16)
      : parseInt(cleanHex.slice(0, 2), 16);
    const g = cleanHex.length === 3 
      ? parseInt(cleanHex[1] + cleanHex[1], 16)
      : parseInt(cleanHex.slice(2, 4), 16);
    const b = cleanHex.length === 3 
      ? parseInt(cleanHex[2] + cleanHex[2], 16)
      : parseInt(cleanHex.slice(4, 6), 16);

    return {
      r,
      g,
      b,
      a: opacity
    };
  };

  const handleChange = (colorResult: ColorResult) => {
    onChange(colorResult.hex, colorResult.rgb.a || opacity);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <button
        onClick={(e) => onOpen(e.currentTarget)}
        className="w-full flex items-center gap-2 border rounded-sm px-3 py-2 hover:bg-gray-50"
      >
        <div
          className="w-6 h-6 rounded-sm border"
          style={{ 
            backgroundColor: showMultipleValues ? '#e5e7eb' : color,
            opacity: opacity 
          }}
        />
        <span className="text-sm text-gray-600">
          {showMultipleValues ? 'Multiple values' : color}
        </span>
      </button>
      {onPickColor && (
        <button
          type="button"
          onClick={onPickColor}
          className={`w-10 h-10 flex items-center justify-center rounded ${
            isPickingColor 
              ? 'bg-blue-100 text-blue-600' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
          }`}
        >
          <MdColorize size={20} />
        </button>
      )}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0" 
            onClick={onClose}
          />
          <div 
            className="fixed z-50"
            style={{ 
              left: position.x,
              top: position.y
            }}
          >
            <SketchPicker
              color={getRgbaFromHexAndOpacity(color, opacity)}
              onChange={handleChange}
            />
          </div>
        </>
      )}
    </div>
  );
}

function isLightColor(color: string): boolean {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
} 